import { buildOpsDayPreview, calculateDistanceKm } from "../../../domain/simulation";
import { resolveAirportCode } from "../../../domain/data/worldQueries";
import { resolveFlightPlanForLeg, resolveOccurrenceForLeg } from "../../../domain/data/scheduleQueries";
import { SERVICE_DAY_LABELS } from "../../../domain/rules/schedule";
import type { ActiveOpsDay, AirportServiceRecord, FlightInstance, FlightOccurrence, FlightPlan, GlobalEvent, PlayerRotation, SaveGame, ScheduleSource, WorldData } from "../../../domain/types";
import type { AirportDataObservation, AirportDataSummary, ScheduledFlightGroup } from "./playerTypes";


export function tryBuildOpsDayPreview(save: SaveGame, schedule: ScheduleSource, activeEvents: GlobalEvent[], world: WorldData): ActiveOpsDay | undefined {
  try {
    return buildOpsDayPreview(save, schedule, activeEvents);
  } catch {
    return undefined;
  }
}

export function releaseQueueFlights(ops: ActiveOpsDay | undefined): FlightInstance[] {
  if (!ops) return [];
  return [...ops.flights]
    .filter((flight) => flight.status === "awaiting_release" || flight.status === "delayed" || flight.status === "needs_recovery")
    .sort((a, b) => a.estimatedDepartureMinute - b.estimatedDepartureMinute || a.flightNo.localeCompare(b.flightNo));
}

export function safeDistance(world: WorldData, originInput: string, destinationInput: string): number | null {
  try {
    return calculateDistanceKm(resolveAirportCode(originInput, world), resolveAirportCode(destinationInput, world));
  } catch {
    return null;
  }
}

export function rotationContinuity(items: Array<Pick<FlightOccurrence, "origin" | "destination">>): { ok: boolean; message: string } {
  if (items.length === 0) {
    return { ok: false, message: "请选择至少一条 GM 定期航班。" };
  }
  for (let index = 1; index < items.length; index += 1) {
    if (items[index - 1].destination !== items[index].origin) {
      return { ok: false, message: `航段链不连续：${items[index - 1].destination} 无法衔接 ${items[index].origin}。` };
    }
  }
  return { ok: true, message: `${items.length} 段航班可保存为自动航班串规则。` };
}

export function rotationPathLabel(rotation: PlayerRotation, schedule: ScheduleSource): string {
  const items = [...rotation.legs]
    .sort((a, b) => a.sequence - b.sequence)
    .map((leg) => resolveOccurrenceForLeg(leg, schedule) ?? resolveFlightPlanForLeg(leg, schedule))
    .filter(Boolean) as Array<Pick<FlightOccurrence, "origin" | "destination">>;
  return rotationPathFromItems(items);
}

function rotationPathFromItems(items: Array<Pick<FlightOccurrence, "origin" | "destination">>): string {
  if (items.length === 0) {
    return "等待 GM 航班";
  }
  const originPath = items.map((item) => item.origin).join("-");
  const finalDestination = items.at(-1)?.destination ?? "";
  return `${originPath}-${finalDestination}`;
}

export function rotationServiceDaysLabel(rotation: PlayerRotation, schedule: ScheduleSource): string {
  const firstOccurrence = resolveOccurrenceForLeg(rotation.legs[0], schedule);
  if (firstOccurrence) {
    return `${SERVICE_DAY_LABELS[firstOccurrence.serviceDay]} D${firstOccurrence.day}`;
  }
  const plan = resolveFlightPlanForLeg(rotation.legs[0], schedule);
  if (!plan) {
    return "等待首段";
  }
  return plan.serviceDays.map((day) => SERVICE_DAY_LABELS[day]).join("/") || "等待首段";
}

export function groupScheduledFlights(flightPlans: FlightPlan[]): ScheduledFlightGroup[] {
  return flightPlans
    .filter((item) => item.active && !item.cancelledAt)
    .map((plan) => {
      return {
        id: plan.id,
        flightNo: plan.flightNo,
        origin: plan.origin,
        destination: plan.destination,
        departureTimeLocal: plan.departureTimeLocal,
        serviceDaysLabel: plan.serviceDays.map((day) => SERVICE_DAY_LABELS[day]).join("/"),
        priority: plan.priority,
        planIds: [plan.id]
      };
    })
    .sort((a, b) => b.priority - a.priority || a.flightNo.localeCompare(b.flightNo));
}

export function activeAircraftFlight(save: SaveGame, aircraftId: string): FlightInstance | undefined {
  return save.activeOpsDay?.flights.find((flight) => flight.aircraftId === aircraftId && !["landed", "cancelled", "failed"].includes(flight.status));
}

export function nextAircraftFlight(flights: FlightInstance[], aircraftId: string): FlightInstance | undefined {
  return flights
    .filter((flight) => flight.aircraftId === aircraftId && !["landed", "cancelled", "failed"].includes(flight.status))
    .sort((a, b) => a.estimatedDepartureMinute - b.estimatedDepartureMinute)[0];
}
